/**
 * VersionCheck — decide se a aba pode buscar a versão nova do app AGORA.
 *
 * ── Por que existe ──────────────────────────────────────────────────────────
 *
 * Depois que a publicação passou a mandar `no-cache` no `index.html`
 * (`.gitlab/scripts/deploy-s3-retencao.sh`), quem dá F5, fecha e abre, ou digita
 * a URL, já recebe a build mais recente. Falta um caso, e é o mais comum de
 * todos: **a aba que ficou aberta**. Ela segue rodando o JS que carregou de
 * manhã, sem nenhum motivo para desconfiar de que existe algo mais novo — até
 * pedir um chunk que a poda do S3 já removeu e quebrar com `ChunkLoadError`.
 *
 * Este arquivo é a decisão: comparar a release embarcada no bundle
 * (`BUILD_INFO.release`) com a do `version.json` publicado ao lado do build e
 * responder "recarrega" ou "não recarrega, e por quê".
 *
 * ── O freio vem ANTES do gate de segurança, e é keyed na release ALVO ───────
 *
 * Um laço de reload aqui derruba o site inteiro, e o caminho para ele é curto:
 * se um service worker congelado servir um `index.html` fóssil, o app sobe numa
 * release velha, lê a release nova no `version.json`, recarrega, recebe o mesmo
 * fóssil e recarrega de novo — para sempre, sem um único erro no console.
 *
 * Por isso o freio não conta reloads: ele guarda **para qual release** já
 * recarregamos. Se já tentamos ir para a `abc123` e continuamos fora dela, o
 * reload não resolve — insistir só piora. Um teto duro por sessão e um cooldown
 * completam, mas quem defende o caso real é a chave por release.
 *
 * Consequência de projeto: sem `sessionStorage` não há freio possível, e sem
 * freio NÃO se recarrega. Perder o frescor é um incômodo; o laço é um incidente.
 *
 * ── O gate de segurança ─────────────────────────────────────────────────────
 *
 * O contrato com o usuário é: "se eu já estava fazendo um pedido, me deixe
 * terminar". Recarregar no meio do checkout perde o carrinho e é pior do que a
 * tela velha que a atualização vinha consertar. Então o reload só acontece com
 * carrinho vazio, fora do fluxo de fechamento e sem formulário sujo — nas
 * demais situações a decisão é ADIAR, e a próxima oportunidade chega sozinha
 * (troca de rota, volta ao foco, ou o próprio fim do pedido).
 */

/** Só o que este módulo usa de `sessionStorage` — facilita o teste. */
export interface StorageMinimo {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * O que a decisão precisa saber sobre a tela neste instante. Vem de fora
 * (serviço Angular) para que a decisão continue pura e testável sem TestBed.
 */
export interface ContextoAtualizacao {
  /** Há item no carrinho? Um carrinho montado é trabalho do usuário. */
  temItensNoCarrinho: boolean;
  /** URL corrente (`router.url`), usada para detectar o fluxo de fechamento. */
  url: string;
  /** Algum formulário na tela tem alteração não salva? */
  formSujo: boolean;
}

/**
 * Veredito. Viaja na telemetria, então cada valor precisa distinguir uma CAUSA
 * diferente — sem isso não dá para saber se a atualização não acontece porque
 * ninguém tem versão nova ou porque o gate adia para sempre.
 */
export type DecisaoAtualizacao =
  | 'sem_novidade'      // release publicada == release em execução
  | 'sem_versao'        // não consegui ler o version.json (rede, 404, JSON inválido)
  | 'atualiza'          // pode recarregar agora
  | 'adiado_carrinho'   // tem item no carrinho
  | 'adiado_checkout'   // está numa etapa do fechamento do pedido
  | 'adiado_form'       // formulário com alteração não salva
  | 'freio_repetido'    // já recarregamos para ESTA release e não chegamos nela
  | 'freio_cooldown'    // recarregou há pouco
  | 'freio_max'         // teto de reloads da sessão
  | 'sem_freio';        // sem sessionStorage: não há como frear, então não recarrega

/**
 * Último segmento das rotas que compõem o fechamento do pedido
 * (`src/app/app-routing.module.ts`). Todas são rotas-folha sob o slug da loja,
 * por isso a comparação é com o ÚLTIMO segmento e não com a URL inteira: uma
 * loja chamada `payment` faria `/app/payment/menu` parecer checkout.
 */
export const ROTAS_DE_FECHAMENTO = [
  'shopping-cart',
  'delivery',
  'payment',
  'order-summary',
  'credit-cards',
];

interface EstadoAtualizacao {
  /** Release para a qual esta sessão já disparou um reload. */
  para: string;
  /** Epoch (ms) do último reload. */
  ts: number;
  /** Reloads por versão nesta sessão. */
  n: number;
}

const ESTADO_ZERO: EstadoAtualizacao = { para: '', ts: 0, n: 0 };

/**
 * `sessionStorage` acessível? O acesso à propriedade já lança `SecurityError`
 * com cookies bloqueados (iOS/Safari, webview embutida), e o `setItem` lança em
 * modo privado antigo — por isso a sonda é uma escrita de verdade.
 */
function sessionStorageSeguro(): StorageMinimo | null {
  try {
    const s = window.sessionStorage;
    if (!s) return null;
    const sonda = 'mp:probe-version';
    s.setItem(sonda, '1');
    s.removeItem(sonda);
    return s;
  } catch (e) {
    return null;
  }
}

/** É uma etapa do fechamento do pedido? */
export function ehRotaDeFechamento(url: string): boolean {
  const limpa = String(url || '').split('?')[0].split('#')[0];
  const segmentos = limpa.split('/').filter((s) => s.length > 0);
  const ultimo = segmentos.length ? segmentos[segmentos.length - 1] : '';
  return ROTAS_DE_FECHAMENTO.indexOf(ultimo) !== -1;
}

export class VersionCheck {
  static readonly STORAGE_KEY = 'mp:version-reload';
  /** Um reload por versão a cada 10 min — mesma ordem do ChunkLoadRecovery. */
  static readonly COOLDOWN_MS = 600000;
  /** Teto por sessão. Três deploys durante a mesma aba já é caso extremo. */
  static readonly MAX_RELOADS = 3;

  constructor(
    private storage: StorageMinimo | null = sessionStorageSeguro(),
    private recarregarPagina: () => void = () => window.location.reload(),
    private agora: () => number = () => Date.now(),
  ) { }

  /**
   * Decide SEM efeito colateral.
   *
   * Separado de `recarregar()` pelo mesmo motivo do `ChunkLoadRecovery`: quem
   * chama precisa registrar o veredito na telemetria ANTES de disparar o
   * reload, senão o evento que explica a decisão morre junto com a página.
   *
   * @param releaseEmExecucao `BUILD_INFO.release` — o que está rodando.
   * @param releasePublicada  o que o `version.json` diz que está no ar.
   */
  decidir(
    releaseEmExecucao: string,
    releasePublicada: string | null,
    contexto: ContextoAtualizacao,
  ): DecisaoAtualizacao {
    const publicada = String(releasePublicada || '');
    if (!publicada) return 'sem_versao';
    if (!releaseEmExecucao || publicada === releaseEmExecucao) return 'sem_novidade';

    // ── freio antes do gate ───────────────────────────────────────────────────
    // Se o freio está armado a resposta é "não recarrega" independentemente do
    // que a tela esteja mostrando, e responder 'adiado_carrinho' aqui esconderia
    // um laço em potencial atrás de uma causa benigna.
    if (!this.storage) return 'sem_freio';
    const estado = this.lerEstado();
    if (estado.n >= VersionCheck.MAX_RELOADS) return 'freio_max';
    if (estado.para && estado.para === publicada) {
      // Já recarregamos MIRANDO esta release e continuamos fora dela: o reload
      // não é o remédio (índice fóssil no service worker, CDN servindo velho).
      return 'freio_repetido';
    }
    if (estado.ts && this.agora() - estado.ts < VersionCheck.COOLDOWN_MS) {
      return 'freio_cooldown';
    }

    // ── gate de segurança: o pedido em andamento manda ────────────────────────
    if (contexto.temItensNoCarrinho) return 'adiado_carrinho';
    if (ehRotaDeFechamento(contexto.url)) return 'adiado_checkout';
    if (contexto.formSujo) return 'adiado_form';

    return 'atualiza';
  }

  /**
   * Registra o alvo e recarrega. Gravar ANTES é o que arma o freio: se a página
   * nova voltar na mesma release, `decidir` já responde 'freio_repetido'.
   */
  recarregar(releaseAlvo: string): void {
    const estado = this.lerEstado();
    this.gravarEstado({ para: String(releaseAlvo || ''), ts: this.agora(), n: estado.n + 1 });
    this.recarregarPagina();
  }

  private lerEstado(): EstadoAtualizacao {
    try {
      const cru = this.storage ? this.storage.getItem(VersionCheck.STORAGE_KEY) : null;
      if (!cru) return ESTADO_ZERO;
      const parsed = JSON.parse(cru);
      // Estado corrompido vale zero em tudo MENOS no freio: preferir o valor
      // conservador aqui significaria travar a atualização para sempre por causa
      // de um byte torto, então o campo `para` só vale se for string.
      const para = typeof parsed.para === 'string' ? parsed.para : '';
      const ts = typeof parsed.ts === 'number' && isFinite(parsed.ts) ? parsed.ts : 0;
      const n = typeof parsed.n === 'number' && isFinite(parsed.n) ? parsed.n : 0;
      return { para, ts, n };
    } catch (e) {
      return ESTADO_ZERO;
    }
  }

  private gravarEstado(estado: EstadoAtualizacao): void {
    try {
      if (this.storage) {
        this.storage.setItem(VersionCheck.STORAGE_KEY, JSON.stringify(estado));
      }
    } catch (e) {
      // Storage cheio no meio do caminho: o reload segue e o pior caso é o de
      // hoje (aba com build velha). `decidir` já exigiu storage legível.
    }
  }
}
